import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.game');
import { AfterViewInit, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Service } from "src/libs/portal/season/service";
import * as THREE from 'three';

type Axis = 'x' | 'y' | 'z';
type LevelStatus = 'playable' | 'locked';
type PieceStatus = 'tray' | 'workspace' | 'placed';
type Coord = [number, number, number];

interface GridSize {
    x: number;
    y: number;
    z: number;
}

interface PieceData {
    id: string;
    name: string;
    symbol: string;
    color: string;
    cells: Coord[];
}

interface GamePiece extends PieceData {
    placed: boolean;
    status: PieceStatus;
    orientationIndex: number;
    position?: Coord;
    workspacePosition?: Coord;
    orientations: Coord[][];
}

interface LevelData {
    id: number;
    name: string;
    shortName: string;
    description: string;
    gridSize: GridSize;
    targetCells: 'all';
    status: LevelStatus;
    lockReason?: string;
    pieces: PieceData[];
}

interface BoardCell {
    pieceId: string;
    color: string;
}

interface PlacementRecord {
    pieceId: string;
    position: Coord;
    orientationIndex: number;
}

interface RenderCube {
    key: string;
    color: string;
    transform: string;
    kind: 'target' | 'solution' | 'placed' | 'ghost-valid' | 'ghost-invalid' | 'piece';
}

interface SolverPlacement extends PlacementRecord {
    cellIndexes: number[];
}

interface PreviewScene {
    host: HTMLElement;
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    root: THREE.Group;
}

const EXPECTED_ORIENTATION_COUNTS: Record<string, number> = {
    'soma-v': 12,
    'soma-l': 24,
    'soma-t': 12,
    'soma-z': 12,
    'soma-p': 12,
    'soma-n': 12,
    'soma-corner': 8
};

const LEVELS: LevelData[] = [
    {
        id: 1,
        name: 'Level 1 - Soma Cube',
        shortName: 'Level 1',
        description: 'Verified 3x3x3 Soma Cube starter puzzle.',
        gridSize: { x: 3, y: 3, z: 3 },
        targetCells: 'all',
        status: 'playable',
        pieces: [
            { id: 'soma-v', symbol: 'V', name: 'V Tricube', color: '#8b5cf6', cells: [[0, 0, 0], [1, 0, 0], [0, 1, 0]] },
            { id: 'soma-l', symbol: 'L', name: 'L Tetracube', color: '#2563eb', cells: [[0, 0, 0], [0, 1, 0], [0, 2, 0], [1, 0, 0]] },
            { id: 'soma-t', symbol: 'T', name: 'T Tetracube', color: '#16a34a', cells: [[0, 0, 0], [1, 0, 0], [2, 0, 0], [1, 1, 0]] },
            { id: 'soma-z', symbol: 'Z', name: 'Z Tetracube', color: '#f97316', cells: [[0, 0, 0], [1, 0, 0], [1, 1, 0], [2, 1, 0]] },
            { id: 'soma-p', symbol: 'R', name: 'Right Screw', color: '#dc2626', cells: [[0, 0, 0], [1, 0, 0], [1, 1, 0], [1, 1, 1]] },
            { id: 'soma-n', symbol: 'S', name: 'Left Screw', color: '#0891b2', cells: [[0, 0, 0], [1, 0, 0], [0, 1, 0], [0, 1, 1]] },
            { id: 'soma-corner', symbol: 'C', name: 'Corner Tetracube', color: '#eab308', cells: [[0, 0, 0], [1, 0, 0], [0, 1, 0], [0, 0, 1]] }
        ]
    },
    {
        id: 2,
        name: 'Level 2 - Bedlam Cube',
        shortName: 'Level 2',
        description: 'Verified 4x4x4 Bedlam Cube set with twelve pentacubes and one tetracube.',
        gridSize: { x: 4, y: 4, z: 4 },
        targetCells: 'all',
        status: 'playable',
        pieces: [
            { id: 'bedlam-flat-r', symbol: '0', name: 'Flat R', color: '#38bdf8', cells: [[0, 0, 0], [1, 0, 0], [2, 0, 0], [1, -1, 0], [2, 1, 0]] },
            { id: 'bedlam-x-plus', symbol: '1', name: 'Flat X Plus', color: '#ef4444', cells: [[0, 0, 0], [1, 0, 0], [2, 0, 0], [1, -1, 0], [1, 1, 0]] },
            { id: 'bedlam-flat-w', symbol: '2', name: 'Flat W', color: '#facc15', cells: [[0, 0, 0], [1, 0, 0], [1, 1, 0], [2, 1, 0], [2, 2, 0]] },
            { id: 'bedlam-bent-w-tip', symbol: '3', name: 'Bent W Tip', color: '#f97316', cells: [[0, 0, 0], [1, 0, 0], [1, 1, 0], [2, 1, 0], [2, 1, 1]] },
            { id: 'bedlam-folded-x', symbol: '4', name: 'Folded X', color: '#a3e635', cells: [[0, 0, 0], [1, 0, 0], [2, 0, 0], [1, -1, 0], [1, 0, 1]] },
            { id: 'bedlam-z-bump', symbol: '5', name: 'Z Bump', color: '#ec4899', cells: [[0, 0, 0], [1, 0, 0], [-1, -1, 0], [0, -1, 0], [0, 0, 1]] },
            { id: 'bedlam-bent-t', symbol: '6', name: 'Bent T', color: '#22c55e', cells: [[0, 0, 0], [1, 0, 0], [2, 0, 0], [1, 1, 0], [1, 1, 1]] },
            { id: 'bedlam-tall-l-bump', symbol: '7', name: 'Tall L Bump', color: '#6366f1', cells: [[0, 0, 0], [1, 0, 0], [2, 0, 0], [0, -1, 0], [0, 0, 1]] },
            { id: 'bedlam-twisted-z', symbol: '8', name: 'Twisted Z', color: '#14b8a6', cells: [[0, 0, 0], [1, 0, 0], [2, 0, 0], [0, -1, 0], [2, 0, 1]] },
            { id: 'bedlam-l-bump-end', symbol: '9', name: 'L Bump End', color: '#eab308', cells: [[0, 0, 0], [1, 0, 0], [2, 0, 0], [2, -1, 0], [2, -1, 1]] },
            { id: 'bedlam-squiggle-a', symbol: 'A', name: 'Blue Squiggle', color: '#0ea5e9', cells: [[0, 0, 0], [1, 0, 0], [1, 1, 0], [1, 1, 1], [2, 1, 1]] },
            { id: 'bedlam-bent-r-tip', symbol: 'B', name: 'Bent R Tip', color: '#8b5cf6', cells: [[0, 0, 0], [1, 0, 0], [2, 0, 0], [0, -1, 0], [1, 0, 1]] },
            { id: 'bedlam-squiggle-c', symbol: 'C', name: 'Tetra Squiggle', color: '#fb7185', cells: [[0, 0, 0], [1, 0, 0], [0, -1, 0], [0, -1, 1]] }
        ]
    },
    {
        id: 3,
        name: 'Level 3 - Pentacube Packing',
        shortName: 'Level 3',
        description: '5x5x5 puzzle data is locked until a verified packing solution is provided.',
        gridSize: { x: 5, y: 5, z: 5 },
        targetCells: 'all',
        status: 'locked',
        lockReason: 'Coming Soon - verified 25 pentacube packing data required.',
        pieces: []
    }
];

@Component({
    selector: 'wiz-page-game',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.game/view.scss */
:host {
  display: block;
  height: 100vh;
  overflow: hidden;
  background: #07111f;
  color: #e5edf7;
}

:host * {
  box-sizing: border-box;
}

.game-shell {
  height: 100vh;
  overflow: hidden;
  background: radial-gradient(circle at 18% 12%, rgba(56, 189, 248, 0.22), transparent 28%), radial-gradient(circle at 78% 14%, rgba(34, 197, 94, 0.16), transparent 24%), linear-gradient(140deg, #0f172a 0%, #09111f 42%, #030712 100%);
  user-select: none;
}

.game-shell.dragging {
  cursor: grabbing;
}

.game-shell.space-mode {
  cursor: crosshair;
}

.game-header {
  height: 88px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 0 32px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(3, 7, 18, 0.58);
  backdrop-filter: blur(18px);
}

.brand-lockup,
.hud,
.level-card-top,
.panel-title-row,
.piece-card,
.piece-info,
.selected-meta,
.axis-row,
.clear-banner {
  display: flex;
  align-items: center;
}

.brand-lockup {
  gap: 14px;
}

.brand-mark {
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  color: #e0f2fe;
  background: linear-gradient(145deg, #0284c7, #22c55e);
  box-shadow: 0 18px 36px rgba(14, 165, 233, 0.28);
}

.brand-mark svg {
  width: 26px;
  height: 26px;
}

.brand-lockup h1 {
  margin: 0;
  font-size: 24px;
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: 0;
  color: #f8fafc;
}

.brand-lockup p {
  margin: 6px 0 0;
  font-size: 13px;
  color: #94a3b8;
}

.hud {
  gap: 12px;
}

.hud-pill {
  min-width: 126px;
  padding: 10px 14px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.66);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.hud-pill span {
  display: block;
  margin-bottom: 4px;
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0;
}

.hud-pill strong {
  font-size: 16px;
  color: #f8fafc;
}

.game-layout {
  height: calc(100vh - 158px);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(420px, 480px);
  gap: 18px;
  padding: 18px;
  min-width: 0;
  overflow: hidden;
}

.level-strip {
  height: 70px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding: 12px 18px 0;
}

.game-panel,
.board-card,
.clear-banner {
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(15, 23, 42, 0.54);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
}

.game-panel {
  min-height: 0;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  border-radius: 24px;
  padding: 16px;
}

.left-panel {
  grid-column: 2;
  grid-row: 1;
  display: flex;
  flex-direction: column;
}

.board-stage {
  grid-column: 1;
  grid-row: 1;
}

.panel-block {
  padding: 16px;
  border-radius: 20px;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.16);
}

.panel-block + .panel-block {
  margin-top: 14px;
}

.panel-title-row {
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.panel-title-row h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: #f8fafc;
}

.panel-title-row span {
  font-size: 12px;
  color: #94a3b8;
}

.level-list {
  display: grid;
  gap: 10px;
}

.level-card {
  width: 100%;
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 16px;
  text-align: left;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(30, 41, 59, 0.62);
  color: #cbd5e1;
  transition: border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease;
}

.level-card:hover {
  transform: translateY(-1px);
  border-color: rgba(56, 189, 248, 0.46);
}

.level-card-active {
  border-color: rgba(56, 189, 248, 0.9);
  box-shadow: 0 0 0 1px rgba(56, 189, 248, 0.2), 0 0 28px rgba(56, 189, 248, 0.18);
}

.level-card-locked {
  opacity: 0.64;
}

.level-card-unlocked {
  border-color: rgba(34, 197, 94, 0.54);
  box-shadow: 0 0 22px rgba(34, 197, 94, 0.12);
}

.level-card-top {
  justify-content: space-between;
}

.level-card-top span,
.level-card > span {
  font-size: 15px;
  font-weight: 800;
  color: #f8fafc;
}

.level-card-top small,
.level-card > small {
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(34, 197, 94, 0.16);
  color: #86efac;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.level-card-locked small {
  background: rgba(148, 163, 184, 0.18);
  color: #cbd5e1;
}

.level-card-unlocked small {
  background: rgba(34, 197, 94, 0.18);
  color: #bbf7d0;
}

.level-card p {
  margin: 9px 0 0;
  font-size: 12px;
  line-height: 1.55;
  color: #94a3b8;
}

.piece-tray {
  display: grid;
  gap: 12px;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 2px;
}

.tray-block {
  min-height: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
}

.piece-card {
  position: relative;
  width: 100%;
  min-height: 136px;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  padding: 12px 16px 12px 12px;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(30, 41, 59, 0.62);
  color: #e2e8f0;
  text-align: left;
  cursor: grab;
  transition: transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease, opacity 140ms ease;
}

.piece-symbol {
  position: absolute;
  left: 18px;
  top: 18px;
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: color-mix(in srgb, var(--piece-color), #020617 22%);
  color: #ffffff;
  font-size: 12px;
  font-weight: 900;
  box-shadow: 0 0 18px color-mix(in srgb, var(--piece-color), transparent 55%);
}

.piece-card:hover:not(:disabled),
.piece-card-selected {
  transform: translateY(-1px);
  border-color: rgba(125, 211, 252, 0.85);
  box-shadow: 0 0 30px rgba(14, 165, 233, 0.22);
}

.piece-card:active {
  cursor: grabbing;
}

.piece-card-placed {
  cursor: default;
  opacity: 0.42;
}

.piece-card-workspace {
  border-color: rgba(34, 197, 94, 0.42);
}

.piece-card em {
  font-size: 11px;
  color: #86efac;
  font-style: normal;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0;
}

.piece-info {
  min-width: 0;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: 7px;
}

.piece-info strong {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 16px;
  color: #f8fafc;
}

.piece-info span {
  font-size: 13px;
  color: #94a3b8;
}

.board-stage {
  min-width: 0;
  overflow: hidden;
}

.board-card {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 28px;
}

.board-toolbar {
  min-height: 76px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.16);
}

.board-toolbar h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 900;
  color: #f8fafc;
}

.board-toolbar p {
  margin: 5px 0 0;
  font-size: 13px;
  color: #94a3b8;
}

.camera-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.camera-controls button,
.rotate-grid button,
.axis-row button,
.soft-button,
.hint-button,
.place-button,
.clear-banner button {
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 14px;
  background: rgba(30, 41, 59, 0.78);
  color: #e2e8f0;
  font-weight: 800;
  font-size: 13px;
  transition: transform 120ms ease, border-color 120ms ease, background 120ms ease, opacity 120ms ease;
}

.camera-controls button {
  min-height: 44px;
  padding: 0 14px;
}

.camera-controls button:hover:not(:disabled),
.rotate-grid button:hover:not(:disabled),
.axis-row button:hover:not(:disabled),
.hint-button:hover:not(:disabled),
.soft-button:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: rgba(125, 211, 252, 0.65);
  background: rgba(51, 65, 85, 0.92);
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.board-dropzone {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: radial-gradient(circle at 50% 38%, rgba(8, 47, 73, 0.95), transparent 38%), linear-gradient(180deg, rgba(15, 23, 42, 0.34), rgba(2, 6, 23, 0.95));
}

.board-dropzone.drag-hot {
  box-shadow: inset 0 0 0 2px rgba(125, 211, 252, 0.28);
}

.scene-light {
  position: absolute;
  border-radius: 999px;
  filter: blur(36px);
  pointer-events: none;
}

.scene-light-a {
  width: 280px;
  height: 280px;
  left: 18%;
  top: 12%;
  background: rgba(56, 189, 248, 0.18);
}

.scene-light-b {
  width: 320px;
  height: 320px;
  right: 12%;
  top: 18%;
  background: rgba(34, 197, 94, 0.12);
}

.cube-container {
  position: absolute;
  inset: 0;
  perspective: 1100px;
  perspective-origin: 50% 42%;
}

.three-board {
  position: absolute;
  inset: 0;
  cursor: grab;
}

.three-board:active {
  cursor: grabbing;
}

.three-preview,
.three-drag {
  width: 100%;
  height: 100%;
}

.three-board canvas,
.three-preview canvas,
.three-drag canvas,
.piece-thumb canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

.board-world,
.piece-world {
  position: absolute;
  left: 50%;
  top: 50%;
  transform-style: preserve-3d;
}

.board-world {
  --cube-size: 62px;
  transition: transform 180ms ease;
}

.piece-world {
  --cube-size: 20px;
  transform: rotateX(58deg) rotateZ(-42deg);
}

.piece-world-tray {
  --cube-size: 20px;
}

.piece-world-selected {
  --cube-size: 34px;
}

.piece-world-drag {
  --cube-size: 28px;
}

.piece-thumb,
.selected-preview {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  background: radial-gradient(circle at 35% 24%, rgba(255, 255, 255, 0.14), transparent 32%), rgba(2, 6, 23, 0.56);
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.piece-thumb {
  width: 180px;
  height: 112px;
  flex: 0 0 auto;
  align-self: center;
  margin: 0;
}

.selected-preview {
  height: 230px;
  cursor: grab;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.piece-thumb .piece-world,
.selected-preview .piece-world,
.drag-piece .piece-world {
  left: 50%;
  top: 56%;
}

.selected-preview .piece-world {
  top: 58%;
}

.cube-block {
  position: absolute;
  width: var(--cube-size);
  height: var(--cube-size);
  margin-left: calc(var(--cube-size) / -2);
  margin-top: calc(var(--cube-size) / -2);
  transform-style: preserve-3d;
  pointer-events: none;
}

.cube-face {
  position: absolute;
  inset: 0;
  border: 1px solid rgba(15, 23, 42, 0.28);
  background: var(--cube-color);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.38), inset 0 -12px 24px rgba(15, 23, 42, 0.2);
}

.cube-face-front {
  transform: translateZ(calc(var(--cube-size) / 2));
}

.cube-face-back {
  transform: rotateY(180deg) translateZ(calc(var(--cube-size) / 2));
  filter: brightness(0.58);
}

.cube-face-right {
  transform: rotateY(90deg) translateZ(calc(var(--cube-size) / 2));
  filter: brightness(0.82);
}

.cube-face-left {
  transform: rotateY(-90deg) translateZ(calc(var(--cube-size) / 2));
  filter: brightness(0.68);
}

.cube-face-top {
  transform: rotateX(90deg) translateZ(calc(var(--cube-size) / 2));
  filter: brightness(1.18);
}

.cube-face-bottom {
  transform: rotateX(-90deg) translateZ(calc(var(--cube-size) / 2));
  filter: brightness(0.5);
}

.cube-target .cube-face {
  background: rgba(191, 219, 254, 0.026);
  border-color: rgba(191, 219, 254, 0.18);
  box-shadow: inset 0 0 10px rgba(125, 211, 252, 0.035);
}

.cube-solution {
  opacity: 0.22;
  filter: saturate(1.08) drop-shadow(0 0 20px color-mix(in srgb, var(--cube-color), transparent 52%));
}

.cube-solution .cube-face {
  border-color: rgba(255, 255, 255, 0.28);
}

.cube-placed .cube-face,
.cube-piece .cube-face {
  border-color: rgba(15, 23, 42, 0.38);
}

.cube-placed {
  filter: saturate(1.08) drop-shadow(0 18px 22px rgba(0, 0, 0, 0.34));
}

.cube-ghost-valid,
.cube-ghost-invalid {
  opacity: 0.56;
  filter: drop-shadow(0 0 24px color-mix(in srgb, var(--cube-color), transparent 30%));
}

.cube-ghost-valid .cube-face,
.cube-ghost-invalid .cube-face {
  border-color: rgba(255, 255, 255, 0.62);
}

.outer-wire {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 268px;
  height: 268px;
  margin-left: -134px;
  margin-top: -134px;
  border: 2px solid rgba(186, 230, 253, 0.34);
  border-radius: 18px;
  transform-style: preserve-3d;
  box-shadow: 0 0 34px rgba(56, 189, 248, 0.12), inset 0 0 28px rgba(186, 230, 253, 0.055);
  pointer-events: none;
}

.outer-wire-back {
  opacity: 0.26;
}

.outer-wire-front {
  opacity: 0.58;
}

.ghost-status,
.hint-status,
.drag-note,
.space-note {
  position: absolute;
  left: 22px;
  bottom: 22px;
  padding: 11px 14px;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(148, 163, 184, 0.18);
  backdrop-filter: blur(12px);
}

.ghost-status {
  display: flex;
  gap: 8px;
  color: #cbd5e1;
}

.hint-status {
  left: 22px;
  bottom: 76px;
  color: #e0f2fe;
  font-size: 12px;
}

.ghost-status strong.ok {
  color: #86efac;
}

.ghost-status strong.bad {
  color: #fca5a5;
}

.drag-note {
  left: auto;
  right: 22px;
  color: #bae6fd;
  font-size: 12px;
}

.space-note {
  left: auto;
  right: 22px;
  bottom: 76px;
  color: #bbf7d0;
  border-color: rgba(34, 197, 94, 0.32);
  font-size: 12px;
}

.locked-board {
  min-height: 570px;
  display: grid;
  place-items: center;
  text-align: center;
  color: #cbd5e1;
}

.locked-icon {
  width: 64px;
  height: 64px;
  display: grid;
  place-items: center;
  margin: 0 auto 16px;
  border-radius: 20px;
  background: rgba(148, 163, 184, 0.12);
  color: #e2e8f0;
}

.locked-icon svg {
  width: 34px;
  height: 34px;
}

.locked-board h3 {
  margin: 0;
  font-size: 22px;
  color: #f8fafc;
}

.locked-board p {
  max-width: 340px;
  margin: 10px auto 0;
  line-height: 1.55;
  color: #94a3b8;
}

.selected-meta {
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
}

.selected-meta strong {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #f8fafc;
}

.inline-symbol {
  width: 24px;
  height: 24px;
  display: inline-grid;
  place-items: center;
  border-radius: 8px;
  background: color-mix(in srgb, var(--piece-color), #020617 16%);
  color: #ffffff;
  font-size: 12px;
  font-weight: 900;
}

.selected-meta span,
.empty-copy {
  color: #94a3b8;
  font-size: 13px;
}

.orientation-audit {
  margin: 10px 0 0;
  color: #64748b;
  font-size: 11px;
  line-height: 1.45;
}

.rotate-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.rotate-grid button {
  min-height: 48px;
}

.key-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.key-grid div {
  height: 46px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  border: 1px solid rgba(125, 211, 252, 0.22);
  background: rgba(2, 6, 23, 0.36);
  color: #e0f2fe;
  font-size: 18px;
  font-weight: 900;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.keyboard-copy {
  margin: 10px 0 0;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.45;
}

.axis-pad {
  display: grid;
  gap: 12px;
}

.axis-row {
  display: grid;
  grid-template-columns: minmax(62px, 1fr) 56px 76px 56px;
  gap: 8px;
  padding: 10px;
  border-radius: 16px;
  background: rgba(2, 6, 23, 0.28);
  border: 1px solid rgba(148, 163, 184, 0.12);
}

.axis-label {
  flex: 1;
  min-width: 0;
}

.axis-label strong {
  display: block;
  color: #e2e8f0;
  font-size: 13px;
}

.axis-label span {
  display: block;
  margin-top: 3px;
  font-size: 11px;
  color: #64748b;
}

.axis-row button {
  width: 56px;
  height: 38px;
  padding: 0;
  font-size: 12px;
}

.axis-value {
  width: auto;
  text-align: center;
}

.axis-value span {
  display: block;
  color: #f8fafc;
  font-size: 12px;
  font-weight: 800;
}

.axis-value small {
  color: #94a3b8;
  font-size: 11px;
}

.control-bar {
  min-height: 86px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 16px 18px;
  border-top: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(2, 6, 23, 0.38);
}

.control-status {
  min-width: 0;
}

.control-status strong {
  display: block;
  color: #f8fafc;
  font-size: 15px;
}

.control-status span {
  display: block;
  margin-top: 4px;
  color: #94a3b8;
  font-size: 12px;
}

.control-actions,
.action-block {
  display: grid;
  grid-template-columns: 120px 110px 110px;
  gap: 10px;
}

.place-button {
  min-height: 56px;
  background: linear-gradient(135deg, #0284c7, #22c55e);
  border-color: rgba(125, 211, 252, 0.55);
  color: #ffffff;
  font-size: 16px;
  box-shadow: 0 18px 36px rgba(14, 165, 233, 0.24);
}

.place-button:hover:not(:disabled) {
  transform: translateY(-1px);
}

.hint-button {
  min-height: 46px;
  background: rgba(14, 165, 233, 0.16);
  border-color: rgba(56, 189, 248, 0.36);
}

.soft-button {
  min-height: 48px;
}

.clear-banner {
  margin-top: 14px;
  justify-content: space-between;
  padding: 18px;
  border-radius: 20px;
}

.clear-banner h2 {
  margin: 0;
  color: #bbf7d0;
}

.clear-banner p {
  margin: 5px 0 0;
  color: #94a3b8;
}

.clear-banner button {
  min-height: 46px;
  padding: 0 18px;
  background: rgba(34, 197, 94, 0.2);
  border-color: rgba(34, 197, 94, 0.42);
}

.drag-piece {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1000;
  width: 190px;
  height: 150px;
  pointer-events: none;
  transform-style: preserve-3d;
  opacity: 0;
  filter: drop-shadow(0 22px 28px rgba(0, 0, 0, 0.38));
}

.drag-piece.visible {
  opacity: 1;
}

@media (max-width: 1180px) {
  .game-layout {
    height: auto;
    min-height: calc(100vh - 88px);
    grid-template-columns: 1fr;
    overflow: auto;
  }
  .game-panel,
  .board-card {
    height: auto;
    min-height: 0;
  }
  .board-dropzone {
    min-height: 560px;
  }
}
@media (max-width: 720px) {
  .game-header {
    height: auto;
    align-items: flex-start;
    flex-direction: column;
    padding: 18px;
  }
  .hud {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .hud-pill {
    min-width: 0;
  }
  .game-layout {
    padding: 12px;
  }
  .board-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }
  .camera-controls {
    flex-wrap: wrap;
  }
}`],
})
export class PageGameComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('boardHost') public boardHost?: ElementRef<HTMLDivElement>;
    @ViewChild('selectedHost') public selectedHost?: ElementRef<HTMLDivElement>;
    @ViewChild('dragHost') public dragHost?: ElementRef<HTMLDivElement>;
    @ViewChildren('trayHost') public trayHosts?: QueryList<ElementRef<HTMLDivElement>>;

    public levels: LevelData[] = LEVELS;
    public activeLevel: LevelData = LEVELS[0];
    public board: (BoardCell | null)[][][] = [];
    public pieces: GamePiece[] = [];
    public selectedPieceId: string = '';
    public selectedOrientationIndex: number = 0;
    public selectedPosition: Record<Axis, number> = { x: 0, y: 0, z: 0 };
    public unlockedLevelIds: number[] = LEVELS.map((level) => level.id);
    public placedPieces: PlacementRecord[] = [];
    public history: PlacementRecord[] = [];
    public moveCount: number = 0;
    public elapsedSeconds: number = 0;
    public isComplete: boolean = false;
    public validationError: string = '';
    public cameraYaw: number = -38;
    public cameraPitch: number = 38;
    public zoom: number = 1;
    public positionAxes: Axis[] = ['x', 'y', 'z'];
    public cubeFaces: string[] = ['front', 'back', 'right', 'left', 'top', 'bottom'];
    public isDragging: boolean = false;
    public dragPieceId: string = '';
    public dragX: number = 0;
    public dragY: number = 0;
    public dragHasBoardTarget: boolean = false;
    public spaceMoveMode: boolean = false;
    public referenceSolution: PlacementRecord[] = [];
    public showReferenceSolution: boolean = false;
    public hintMessage: string = '';
    public orientationAudit: string = '';

    private timer: any = null;
    private isOrbiting: boolean = false;
    private orbitStartX: number = 0;
    private orbitStartY: number = 0;
    private orbitStartYaw: number = -42;
    private orbitStartPitch: number = 58;
    private boardScene?: THREE.Scene;
    private boardCamera?: THREE.PerspectiveCamera;
    private boardRenderer?: THREE.WebGLRenderer;
    private boardRoot: THREE.Group = new THREE.Group();
    private guideGroup: THREE.Group = new THREE.Group();
    private workspaceGroup: THREE.Group = new THREE.Group();
    private placedGroup: THREE.Group = new THREE.Group();
    private dragGroup: THREE.Group = new THREE.Group();
    private ghostGroup: THREE.Group = new THREE.Group();
    private trayScenes: PreviewScene[] = [];
    private selectedScene?: PreviewScene;
    private dragScene?: PreviewScene;
    private renderQueued: boolean = false;
    private dragWorldPoint: THREE.Vector3 | null = null;

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        this.selectLevel(this.levels[0]);
    }

    public ngAfterViewInit() {
        this.initBoardScene();
        this.initPreviewScenes();
        this.trayHosts?.changes.subscribe(() => {
            this.initTrayScenes();
            this.renderAllThree();
        });
        this.renderAllThree();
    }

    public ngOnDestroy() {
        this.stopTimer();
        this.disposeThree();
    }

    public selectLevel(level: LevelData) {
        if (!this.isLevelUnlocked(level)) return;
        this.stopTimer();
        this.activeLevel = level;
        this.validationError = '';
        this.isDragging = false;
        this.dragPieceId = '';
        this.dragHasBoardTarget = false;
        this.dragWorldPoint = null;
        this.spaceMoveMode = false;
        this.referenceSolution = [];
        this.showReferenceSolution = false;
        this.hintMessage = '';
        this.orientationAudit = '';

        if (level.status !== 'playable') {
            this.board = [];
            this.pieces = [];
            this.selectedPieceId = '';
            this.elapsedSeconds = 0;
            this.moveCount = 0;
            this.isComplete = false;
            this.history = [];
            this.placedPieces = [];
            this.queueThreeRender();
            return;
        }

        this.resetLevel();
    }

    public resetLevel() {
        this.stopTimer();
        this.board = this.createBoard(this.activeLevel.gridSize);
        this.pieces = this.activeLevel.pieces.map((piece) => ({
            ...piece,
            placed: false,
            status: 'tray',
            orientationIndex: 0,
            orientations: this.precomputeUniqueOrientations(piece.cells)
        }));
        this.selectedPieceId = this.pieces[0]?.id || '';
        this.selectedOrientationIndex = 0;
        this.selectedPosition = { x: 0, y: 0, z: 0 };
        this.placedPieces = [];
        this.history = [];
        this.moveCount = 0;
        this.elapsedSeconds = 0;
        this.isComplete = false;
        this.isDragging = false;
        this.dragPieceId = '';
        this.dragHasBoardTarget = false;
        this.dragWorldPoint = null;
        this.spaceMoveMode = false;
        this.showReferenceSolution = false;
        this.hintMessage = '';
        this.orientationAudit = this.buildOrientationAudit(this.pieces);
        this.buildCubeContainer();

        if (!this.validateLevelData(this.activeLevel, this.pieces)) {
            this.validationError = 'Level 1 piece data failed solvability validation. Gameplay is disabled until verified data is provided.';
            console.warn(this.validationError, this.orientationAudit);
            this.selectedPieceId = '';
            this.queueThreeRender();
            return;
        }

        this.referenceSolution = this.findFirstSolution(this.activeLevel, this.pieces) || [];
        this.queueThreeRender();
        this.startTimer();
    }

    public createBoard(gridSize: GridSize): (BoardCell | null)[][][] {
        return Array.from({ length: gridSize.x }, () =>
            Array.from({ length: gridSize.y }, () =>
                Array.from({ length: gridSize.z }, () => null)
            )
        );
    }

    public getAllTargetCells(level: LevelData): Coord[] {
        const cells: Coord[] = [];
        for (let x = 0; x < level.gridSize.x; x++) {
            for (let y = 0; y < level.gridSize.y; y++) {
                for (let z = 0; z < level.gridSize.z; z++) {
                    cells.push([x, y, z]);
                }
            }
        }
        return cells;
    }

    public rotateCells(cells: Coord[], axis: Axis): Coord[] {
        const rotated = cells.map(([x, y, z]) => {
            if (axis === 'x') return [x, -z, y] as Coord;
            if (axis === 'y') return [z, y, -x] as Coord;
            return [-y, x, z] as Coord;
        });
        return this.normalizeCells(rotated);
    }

    public normalizeCells(cells: Coord[]): Coord[] {
        const minX = Math.min(...cells.map(([x]) => x));
        const minY = Math.min(...cells.map(([, y]) => y));
        const minZ = Math.min(...cells.map(([, , z]) => z));
        return cells
            .map(([x, y, z]) => [x - minX, y - minY, z - minZ] as Coord)
            .sort((a, b) => a[0] - b[0] || a[1] - b[1] || a[2] - b[2]);
    }

    public precomputeUniqueOrientations(cells: Coord[]): Coord[][] {
        const transforms: Array<(cell: Coord) => Coord> = [
            ([x, y, z]) => [x, y, z],
            ([x, y, z]) => [x, -z, y],
            ([x, y, z]) => [x, -y, -z],
            ([x, y, z]) => [x, z, -y],
            ([x, y, z]) => [-x, -y, z],
            ([x, y, z]) => [-x, -z, -y],
            ([x, y, z]) => [-x, y, -z],
            ([x, y, z]) => [-x, z, y],
            ([x, y, z]) => [y, z, x],
            ([x, y, z]) => [y, -x, z],
            ([x, y, z]) => [y, -z, -x],
            ([x, y, z]) => [y, x, -z],
            ([x, y, z]) => [-y, -z, x],
            ([x, y, z]) => [-y, -x, -z],
            ([x, y, z]) => [-y, z, -x],
            ([x, y, z]) => [-y, x, z],
            ([x, y, z]) => [z, x, y],
            ([x, y, z]) => [z, -y, x],
            ([x, y, z]) => [z, -x, -y],
            ([x, y, z]) => [z, y, -x],
            ([x, y, z]) => [-z, -x, y],
            ([x, y, z]) => [-z, -y, -x],
            ([x, y, z]) => [-z, x, -y],
            ([x, y, z]) => [-z, y, x]
        ];
        const orientations = new Map<string, Coord[]>();
        transforms.forEach((transform) => {
            const normalized = this.normalizeCells(cells.map(transform));
            orientations.set(this.cellsKey(normalized), normalized);
        });
        return Array.from(orientations.values());
    }

    public getTransformedCells(piece: GamePiece, position: Coord, orientationIndex: number): Coord[] {
        const orientation = piece.orientations[orientationIndex] || piece.orientations[0] || [];
        return orientation.map(([x, y, z]) => [x + position[0], y + position[1], z + position[2]] as Coord);
    }

    public canPlacePiece(board: (BoardCell | null)[][][], cells: Coord[], position: Coord, level: LevelData): boolean {
        const transformed = cells.map(([x, y, z]) => [x + position[0], y + position[1], z + position[2]] as Coord);
        return transformed.every(([x, y, z]) => {
            return Number.isInteger(x)
                && Number.isInteger(y)
                && Number.isInteger(z)
                && x >= 0
                && y >= 0
                && z >= 0
                && x < level.gridSize.x
                && y < level.gridSize.y
                && z < level.gridSize.z
                && board[x]?.[y]?.[z] === null;
        });
    }

    public placePiece(board: (BoardCell | null)[][][], piece: GamePiece, position: Coord) {
        const cells = this.getTransformedCells(piece, position, piece.orientationIndex);
        cells.forEach(([x, y, z]) => {
            board[x][y][z] = { pieceId: piece.id, color: piece.color };
        });
        piece.placed = true;
        piece.status = 'placed';
        piece.position = position;
        piece.workspacePosition = undefined;
    }

    public removePiece(board: (BoardCell | null)[][][], pieceId: string) {
        for (let x = 0; x < board.length; x++) {
            for (let y = 0; y < board[x].length; y++) {
                for (let z = 0; z < board[x][y].length; z++) {
                    if (board[x][y][z]?.pieceId === pieceId) {
                        board[x][y][z] = null;
                    }
                }
            }
        }
    }

    public isLevelComplete(board: (BoardCell | null)[][][], level: LevelData): boolean {
        return this.getAllTargetCells(level).every(([x, y, z]) => board[x]?.[y]?.[z] !== null);
    }

    public getFilledCellCount(board: (BoardCell | null)[][][] = this.board): number {
        let count = 0;
        for (let x = 0; x < board.length; x++) {
            for (let y = 0; y < board[x].length; y++) {
                for (let z = 0; z < board[x][y].length; z++) {
                    if (board[x][y][z]) count++;
                }
            }
        }
        return count;
    }

    public getTargetCellCount(level: LevelData = this.activeLevel): number {
        return level.gridSize.x * level.gridSize.y * level.gridSize.z;
    }

    public selectPiece(piece: GamePiece) {
        if (piece.status === 'placed' || this.isComplete) return;
        this.selectedPieceId = piece.id;
        this.selectedOrientationIndex = piece.orientationIndex;
        this.showReferenceSolution = false;
        this.hintMessage = '';
        this.queueThreeRender();
    }

    public rotateSelected(axis: Axis) {
        const piece = this.selectedPiece;
        if (!piece) return;
        const current = piece.orientations[this.selectedOrientationIndex] || piece.orientations[0];
        const rotated = this.rotateCells(current, axis);
        const index = piece.orientations.findIndex((orientation) => this.cellsKey(orientation) === this.cellsKey(rotated));
        this.selectedOrientationIndex = index >= 0 ? index : this.selectedOrientationIndex;
        piece.orientationIndex = this.selectedOrientationIndex;
        this.queueThreeRender();
    }

    public rotateDirectional(direction: 'left' | 'right' | 'up' | 'down') {
        if (direction === 'left') {
            this.rotateSelected('z');
            return;
        }
        if (direction === 'right') {
            this.rotateSelected('z');
            this.rotateSelected('z');
            this.rotateSelected('z');
            return;
        }
        if (direction === 'up') {
            this.rotateSelected('x');
            return;
        }
        this.rotateSelected('x');
        this.rotateSelected('x');
        this.rotateSelected('x');
    }

    public nextOrientation() {
        const piece = this.selectedPiece;
        if (!piece) return;
        this.selectedOrientationIndex = (this.selectedOrientationIndex + 1) % piece.orientations.length;
        piece.orientationIndex = this.selectedOrientationIndex;
    }

    public setPosition(axis: Axis, value: number | string) {
        const numeric = Number(value);
        if (!Number.isFinite(numeric)) return;
        const max = this.activeLevel.gridSize[axis] - 1;
        this.selectedPosition[axis] = Math.max(0, Math.min(max, Math.trunc(numeric)));
    }

    public setPositionFromEvent(axis: Axis, event: Event) {
        const input = event.target as HTMLInputElement | null;
        this.setPosition(axis, input?.value || 0);
    }

    public positionValue(axis: Axis): number {
        return this.selectedPosition[axis];
    }

    public maxPosition(axis: Axis): number {
        return this.activeLevel.gridSize[axis] - 1;
    }

    public nudgePosition(axis: Axis, delta: number) {
        this.setPosition(axis, this.selectedPosition[axis] + delta);
    }

    public placeSelected() {
        const piece = this.selectedPiece;
        if (!piece || !this.previewValid || this.isComplete) return;
        this.placePieceAtCurrentPosition(piece);
    }

    private placePieceAtCurrentPosition(piece: GamePiece) {
        if (this.isComplete) return;
        const position: Coord = [this.selectedPosition.x, this.selectedPosition.y, this.selectedPosition.z];
        piece.orientationIndex = this.orientationIndexForPiece(piece);
        if (!this.canPlacePiece(this.board, piece.orientations[piece.orientationIndex] || [], position, this.activeLevel)) return;
        this.removePiece(this.board, piece.id);
        this.placedPieces = this.placedPieces.filter((item) => item.pieceId !== piece.id);
        this.placePiece(this.board, piece, position);

        const record = { pieceId: piece.id, position, orientationIndex: piece.orientationIndex };
        this.history.push(record);
        this.placedPieces.push(record);
        this.moveCount++;

        this.isComplete = this.isLevelComplete(this.board, this.activeLevel);
        if (this.isComplete) {
            this.selectedPieceId = '';
            this.stopTimer();
            this.unlockNextLevel();
        } else {
            const nextPiece = this.pieces.find((item) => item.status !== 'placed');
            this.selectedPieceId = nextPiece?.id || '';
            this.selectedOrientationIndex = nextPiece?.orientationIndex || 0;
        }
        this.queueThreeRender();
    }

    public undo() {
        const last = this.history.pop();
        if (!last) return;
        this.removePiece(this.board, last.pieceId);
        const piece = this.pieces.find((item) => item.id === last.pieceId);
        if (piece) {
            piece.placed = false;
            piece.status = 'workspace';
            piece.position = undefined;
            piece.workspacePosition = this.resolveWorkspacePosition(piece, this.workspaceVectorFromCoord(this.defaultWorkspacePosition(piece)));
            piece.orientationIndex = last.orientationIndex;
            this.selectedPieceId = piece.id;
            this.selectedOrientationIndex = last.orientationIndex;
            this.selectedPosition = { x: last.position[0], y: last.position[1], z: last.position[2] };
        }
        this.placedPieces = this.placedPieces.filter((item) => item.pieceId !== last.pieceId);
        this.moveCount = Math.max(0, this.moveCount - 1);
        this.isComplete = false;
        this.startTimer();
        this.queueThreeRender();
    }

    public rotateCamera(delta: number) {
        if (this.isDragging) return;
        this.cameraYaw += delta * 18;
        this.queueThreeRender();
    }

    public zoomBy(delta: number) {
        if (this.isDragging) return;
        this.zoom = Math.max(0.82, Math.min(1.25, Number((this.zoom + delta).toFixed(2))));
        this.queueThreeRender();
    }

    public resetCamera() {
        this.cameraYaw = -38;
        this.cameraPitch = 38;
        this.zoom = 1;
        this.queueThreeRender();
    }

    public startOrbit(event: PointerEvent) {
        if (this.isDragging || event.button !== 0) return;
        if (this.spaceMoveMode) {
            const pickedPieceId = this.pickMovablePieceId(event);
            event.preventDefault();
            if (pickedPieceId) {
                this.pickupMovablePiece(pickedPieceId, event);
            }
            return;
        }
        event.preventDefault();
        this.isOrbiting = true;
        this.orbitStartX = event.clientX;
        this.orbitStartY = event.clientY;
        this.orbitStartYaw = this.cameraYaw;
        this.orbitStartPitch = this.cameraPitch;
    }

    public startDrag(piece: GamePiece, event: PointerEvent) {
        if (piece.status === 'placed' || this.isComplete || this.validationError) return;
        event.preventDefault();
        this.showReferenceSolution = false;
        this.hintMessage = '';
        this.selectPiece(piece);
        if (piece.status === 'tray') {
            piece.status = 'workspace';
            piece.workspacePosition = this.resolveWorkspacePosition(piece, this.workspaceVectorFromCoord(this.defaultWorkspacePosition(piece)));
        }
        this.isDragging = true;
        this.dragPieceId = piece.id;
        this.dragX = event.clientX;
        this.dragY = event.clientY;
        this.dragHasBoardTarget = false;
        this.snapPointerToBoard(event);
        this.updateDragWorldPoint(event);
        this.queueThreeRender();
    }

    @HostListener('window:pointermove', ['$event'])
    public onPointerMove(event: PointerEvent) {
        if (this.isOrbiting && !this.isDragging) {
            this.cameraYaw = this.orbitStartYaw + (event.clientX - this.orbitStartX) * 0.35;
            this.cameraPitch = Math.max(24, Math.min(70, this.orbitStartPitch - (event.clientY - this.orbitStartY) * 0.18));
            this.queueThreeRender();
            return;
        }
        if (!this.isDragging) return;
        this.dragX = event.clientX;
        this.dragY = event.clientY;
        this.snapPointerToBoard(event);
        this.updateDragWorldPoint(event);
        this.queueThreeRender();
    }

    @HostListener('window:pointerup', ['$event'])
    public onPointerUp(event: PointerEvent) {
        if (this.isOrbiting && !this.isDragging) {
            this.isOrbiting = false;
            return;
        }
        if (!this.isDragging) return;
        this.snapPointerToBoard(event);
        const piece = this.activeInteractionPiece;
        const shouldPlace = Boolean(piece && this.dragHasBoardTarget && this.isPlacementValidForPiece(piece));
        const workspacePoint = piece ? this.findWorkspacePoint(event.clientX, event.clientY) : null;
        this.isDragging = false;
        this.dragPieceId = '';
        this.dragHasBoardTarget = false;
        this.dragWorldPoint = null;
        if (shouldPlace && piece) {
            this.placePieceAtCurrentPosition(piece);
        } else if (piece && workspacePoint) {
            this.movePieceToWorkspace(piece, workspacePoint);
        } else {
            this.queueThreeRender();
        }
        this.spaceMoveMode = false;
    }

    @HostListener('window:keydown', ['$event'])
    public onKeyDown(event: KeyboardEvent) {
        if (event.code === 'Space') {
            event.preventDefault();
            if (!event.repeat) {
                this.spaceMoveMode = !this.spaceMoveMode;
                this.queueThreeRender();
            }
            return;
        }
        if (!this.selectedPiece || this.isComplete || this.validationError) return;
        const keyMap: Record<string, 'left' | 'right' | 'up' | 'down'> = {
            ArrowLeft: 'left',
            ArrowRight: 'right',
            ArrowUp: 'up',
            ArrowDown: 'down'
        };
        const direction = keyMap[event.key];
        if (!direction) return;
        event.preventDefault();
        this.showReferenceSolution = false;
        this.hintMessage = '';
        this.rotateDirectional(direction);
    }

    @HostListener('window:keyup', ['$event'])
    public onKeyUp(event: KeyboardEvent) {
        if (event.code !== 'Space') return;
        event.preventDefault();
    }

    @HostListener('window:resize')
    public onWindowResize() {
        this.queueThreeRender();
    }

    public snapPointerToBoard(event: PointerEvent) {
        const boardElement = document.getElementById('polycube-board-dropzone');
        if (!boardElement) return;
        const rect = boardElement.getBoundingClientRect();
        const within = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
        if (!within) {
            this.dragHasBoardTarget = false;
            return;
        }

        const snap = this.findRaycastSnap(event.clientX, event.clientY);
        if (!snap) {
            this.dragHasBoardTarget = false;
            return;
        }

        this.selectedPosition = { x: snap[0], y: snap[1], z: snap[2] };
        this.dragHasBoardTarget = true;
        this.queueThreeRender();
    }

    private updateDragWorldPoint(event: PointerEvent) {
        if (!this.draggingPiece) {
            this.dragWorldPoint = null;
            return;
        }
        if (this.dragHasBoardTarget) {
            this.dragWorldPoint = this.cellToWorld([this.selectedPosition.x, this.selectedPosition.y, this.selectedPosition.z]);
            return;
        }
        this.dragWorldPoint = this.findWorkspacePoint(event.clientX, event.clientY);
    }

    public get selectedPiece(): GamePiece | undefined {
        return this.pieces.find((piece) => piece.id === this.selectedPieceId && piece.status !== 'placed');
    }

    public get draggingPiece(): GamePiece | undefined {
        return this.pieces.find((piece) => piece.id === this.dragPieceId && piece.status !== 'placed');
    }

    public get activeInteractionPiece(): GamePiece | undefined {
        return this.draggingPiece || this.selectedPiece;
    }

    public get previewCells(): Coord[] {
        const piece = this.activeInteractionPiece;
        if (!piece) return [];
        return this.getTransformedCells(piece, [this.selectedPosition.x, this.selectedPosition.y, this.selectedPosition.z], this.orientationIndexForPiece(piece));
    }

    public get shouldShowGhost(): boolean {
        if (!this.activeInteractionPiece || this.isComplete) return false;
        return !this.isDragging || this.dragHasBoardTarget;
    }

    public get previewValid(): boolean {
        if (this.validationError) return false;
        const piece = this.activeInteractionPiece;
        return piece ? this.isPlacementValidForPiece(piece) : false;
    }

    private orientationIndexForPiece(piece: GamePiece): number {
        return piece.id === this.selectedPieceId ? this.selectedOrientationIndex : piece.orientationIndex;
    }

    private isPlacementValidForPiece(piece: GamePiece): boolean {
        const orientation = piece.orientations[this.orientationIndexForPiece(piece)] || [];
        return this.canPlacePiece(this.board, orientation, [this.selectedPosition.x, this.selectedPosition.y, this.selectedPosition.z], this.activeLevel);
    }

    private unlockNextLevel() {
        const nextLevel = this.levels.find((level) => level.id === this.activeLevel.id + 1);
        if (!nextLevel || this.unlockedLevelIds.includes(nextLevel.id)) return;
        this.unlockedLevelIds = [...this.unlockedLevelIds, nextLevel.id];
    }

    public get nextLevel(): LevelData | undefined {
        return this.levels.find((level) => level.id === this.activeLevel.id + 1);
    }

    public get clearMessage(): string {
        const next = this.nextLevel;
        return next ? `${next.shortName} is open.` : 'All available levels are complete.';
    }

    public get nextLevelButtonLabel(): string {
        const next = this.nextLevel;
        return next ? `Open ${next.shortName}` : 'Puzzle Complete';
    }

    public get boardTitle(): string {
        const size = this.activeLevel.gridSize;
        if (this.activeLevel.id === 1) return '3×3×3 Soma Cube';
        if (this.activeLevel.id === 2) return '4×4×4 Bedlam Cube';
        if (this.activeLevel.id === 3) return '5×5×5 Pentacube Packing';
        return `${size.x}×${size.y}×${size.z} Polycube Puzzle`;
    }

    public openNextLevel() {
        const next = this.nextLevel;
        if (next) this.selectLevel(next);
    }

    public get placedCount(): number {
        return this.pieces.filter((piece) => piece.placed).length;
    }

    public get formattedTime(): string {
        const minutes = Math.floor(this.elapsedSeconds / 60).toString().padStart(2, '0');
        const seconds = (this.elapsedSeconds % 60).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    }

    public get boardWorldTransform(): string {
        return `rotateX(${this.cameraPitch}deg) rotateZ(${this.cameraYaw}deg) scale(${this.zoom})`;
    }

    public get outerWireFrontTransform(): string {
        return `${this.boardWorldTransform} translateZ(116px)`;
    }

    public get outerWireBackTransform(): string {
        return `${this.boardWorldTransform} translateZ(-116px)`;
    }

    public get dragTransform(): string {
        return `translate3d(${this.dragX - 95}px, ${this.dragY - 75}px, 0)`;
    }

    public get ghostLabel(): string {
        if (this.isDragging && !this.dragHasBoardTarget) return 'Move over cube';
        return this.previewValid ? 'Valid' : 'Invalid';
    }

    public toggleReferenceSolution() {
        if (!this.referenceSolution.length || this.isComplete) return;
        this.showReferenceSolution = !this.showReferenceSolution;
        this.hintMessage = this.showReferenceSolution
            ? 'Reference solution is shown as faint blocks.'
            : '';
        this.queueThreeRender();
    }

    public hintSelected() {
        const piece = this.selectedPiece;
        if (!piece || this.isComplete) return;
        this.showReferenceSolution = false;
        const reference = this.referenceSolution.find((record) => record.pieceId === piece.id);
        const options = this.getValidPlacements(this.activeLevel, piece);
        const preferred = reference && options.find((option) => option.position.join(',') === reference.position.join(',') && option.orientationIndex === reference.orientationIndex);
        const hint = preferred || options[0];
        if (!hint) {
            this.hintMessage = 'No valid slot for this piece from the current board.';
            return;
        }
        this.selectedOrientationIndex = hint.orientationIndex;
        piece.orientationIndex = hint.orientationIndex;
        this.selectedPosition = { x: hint.position[0], y: hint.position[1], z: hint.position[2] };
        this.hintMessage = `Hint set for ${piece.name}.`;
        this.queueThreeRender();
    }

    public getOrientationCount(piece: GamePiece | undefined = this.selectedPiece): number {
        return piece?.orientations.length || 0;
    }

    public getBoardCubes(): RenderCube[] {
        if (this.activeLevel.status !== 'playable') return [];
        const cubes: RenderCube[] = [];
        this.getAllTargetCells(this.activeLevel).forEach(([x, y, z]) => {
            if (!this.isGuideCell([x, y, z]) || !this.isSparseGuideCell([x, y, z])) return;
            if (this.board[x]?.[y]?.[z]) return;
            cubes.push({
                key: `target-${x}-${y}-${z}`,
                color: '#dbeafe',
                transform: this.cubeTransform([x, y, z], 76),
                kind: 'target'
            });
        });
        if (this.showReferenceSolution) {
            this.referenceSolution.forEach((record) => {
                const piece = this.pieces.find((item) => item.id === record.pieceId);
                if (!piece || piece.placed) return;
                this.getTransformedCells(piece, record.position, record.orientationIndex).forEach(([x, y, z], index) => {
                    if (this.board[x]?.[y]?.[z]) return;
                    cubes.push({
                        key: `solution-${record.pieceId}-${index}-${x}-${y}-${z}`,
                        color: piece.color,
                        transform: this.cubeTransform([x, y, z], 76),
                        kind: 'solution'
                    });
                });
            });
        }
        this.getAllTargetCells(this.activeLevel).forEach(([x, y, z]) => {
            const cell = this.board[x]?.[y]?.[z];
            if (!cell) return;
            cubes.push({
                key: `placed-${x}-${y}-${z}`,
                color: cell.color,
                transform: this.cubeTransform([x, y, z], 76),
                kind: 'placed'
            });
        });
        return cubes;
    }

    public getPieceCubes(piece: GamePiece | undefined, mode: 'tray' | 'selected' | 'drag' = 'tray'): RenderCube[] {
        if (!piece) return [];
        const orientationIndex = piece.id === this.selectedPieceId ? this.selectedOrientationIndex : piece.orientationIndex;
        const cells = piece.orientations[orientationIndex] || piece.orientations[0] || piece.cells;
        const gap = mode === 'selected' ? 42 : mode === 'drag' ? 30 : 24;
        const center = this.cellsCenter(cells);
        return cells.map(([x, y, z], index) => ({
            key: `${piece.id}-${mode}-${index}`,
            color: piece.color,
            transform: this.freeCubeTransform([x - center[0], y - center[1], z - center[2]], gap),
            kind: 'piece'
        }));
    }

    public cubeClass(cube: RenderCube): string {
        return `cube-block cube-${cube.kind}`;
    }

    public faceClass(face: string): string {
        return `cube-face cube-face-${face}`;
    }

    public levelCardClass(level: LevelData): string {
        const base = 'level-card';
        if (this.activeLevel.id === level.id) return `${base} level-card-active`;
        if (!this.isLevelUnlocked(level)) return `${base} level-card-locked`;
        if (this.isUnlockedComingSoon(level)) return `${base} level-card-unlocked`;
        if (level.status === 'locked') return `${base} level-card-locked`;
        return base;
    }

    public levelStatusLabel(level: LevelData): string {
        if (!this.isLevelUnlocked(level)) return 'Locked';
        if (level.status === 'playable') return 'Ready';
        return this.isUnlockedComingSoon(level) ? 'Open' : 'Locked';
    }

    public isLevelUnlocked(level: LevelData): boolean {
        return this.unlockedLevelIds.includes(level.id);
    }

    public isUnlockedComingSoon(level: LevelData): boolean {
        return level.status === 'locked' && this.isLevelUnlocked(level);
    }

    public pieceCardClass(piece: GamePiece): string {
        const classes = ['piece-card'];
        if (piece.id === this.selectedPieceId && piece.status !== 'placed') classes.push('piece-card-selected');
        if (piece.status === 'workspace') classes.push('piece-card-workspace');
        if (piece.status === 'placed') classes.push('piece-card-placed');
        return classes.join(' ');
    }

    public axisLabel(axis: Axis, value: number): string {
        const labels = {
            x: ['Left', 'Center', 'Right'],
            y: ['Front', 'Center', 'Back'],
            z: ['Bottom', 'Middle', 'Top']
        };
        return labels[axis][value] || String(value);
    }

    public axisLongLabel(axis: Axis): string {
        if (axis === 'x') return 'Left / Right';
        if (axis === 'y') return 'Front / Back';
        return 'Bottom / Top';
    }

    public axisControlName(axis: Axis): string {
        if (axis === 'x') return 'Slide';
        if (axis === 'y') return 'Depth';
        return 'Lift';
    }

    public axisNegativeLabel(axis: Axis): string {
        if (axis === 'x') return 'Left';
        if (axis === 'y') return 'Front';
        return 'Down';
    }

    public axisPositiveLabel(axis: Axis): string {
        if (axis === 'x') return 'Right';
        if (axis === 'y') return 'Back';
        return 'Up';
    }

    private initBoardScene() {
        const host = this.boardHost?.nativeElement;
        if (!host || this.boardRenderer) return;

        this.boardScene = new THREE.Scene();
        this.boardCamera = new THREE.PerspectiveCamera(36, 1, 0.1, 100);
        this.boardRenderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        this.boardRenderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        this.boardRenderer.shadowMap.enabled = true;
        this.boardRenderer.shadowMap.type = THREE.PCFSoftShadowMap;
        host.innerHTML = '';
        host.appendChild(this.boardRenderer.domElement);

        const ambient = new THREE.HemisphereLight(0xffffff, 0x0f172a, 2.7);
        const key = new THREE.DirectionalLight(0xffffff, 3.2);
        key.position.set(5, 7, 6);
        key.castShadow = true;
        const fill = new THREE.DirectionalLight(0x7dd3fc, 1.5);
        fill.position.set(-5, 2, -4);
        this.boardScene.add(ambient, key, fill);

        this.boardRoot.add(this.guideGroup, this.workspaceGroup, this.placedGroup, this.dragGroup, this.ghostGroup);
        this.boardScene.add(this.boardRoot);
        this.buildCubeContainer();
        this.resizeBoardRenderer();
    }

    private initPreviewScenes() {
        this.initTrayScenes();
        if (this.selectedHost?.nativeElement && !this.selectedScene) {
            this.selectedScene = this.createPreviewScene(this.selectedHost.nativeElement);
        }
        if (this.dragHost?.nativeElement && !this.dragScene) {
            this.dragScene = this.createPreviewScene(this.dragHost.nativeElement);
        }
    }

    private initTrayScenes() {
        this.disposePreviewScenes(this.trayScenes);
        this.trayScenes = [];
        this.trayHosts?.forEach((host) => {
            this.trayScenes.push(this.createPreviewScene(host.nativeElement));
        });
    }

    private createPreviewScene(host: HTMLElement): PreviewScene {
        host.innerHTML = '';
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 50);
        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        host.appendChild(renderer.domElement);

        const root = new THREE.Group();
        scene.add(root);
        scene.add(new THREE.HemisphereLight(0xffffff, 0x1e293b, 2.4));
        const key = new THREE.DirectionalLight(0xffffff, 2.6);
        key.position.set(4, 5, 5);
        scene.add(key);
        camera.position.set(4.5, 4.2, 6.8);
        camera.lookAt(0, 0, 0);
        this.resizePreviewScene({ host, renderer, scene, camera, root });
        return { host, renderer, scene, camera, root };
    }

    private buildCubeContainer() {
        this.clearGroup(this.guideGroup);
        const { x, y, z } = this.activeLevel.gridSize;
        const width = x + 0.04;
        const height = z + 0.04;
        const depth = y + 0.04;
        const edgeWidth = x + 0.06;
        const edgeHeight = z + 0.06;
        const edgeDepth = y + 0.06;
        const minX = -x / 2;
        const maxX = x / 2;
        const minY = -z / 2;
        const maxY = z / 2;
        const minZ = -y / 2;
        const maxZ = y / 2;
        const guideOpacity = Math.max(0.06, 0.28 - Math.max(x, y, z) * 0.045);
        const box = new THREE.Mesh(
            new THREE.BoxGeometry(width, height, depth),
            new THREE.MeshPhysicalMaterial({
                color: 0xdbeafe,
                transparent: true,
                opacity: Math.max(0.018, 0.065 - Math.max(x, y, z) * 0.01),
                roughness: 0.12,
                metalness: 0,
                transmission: 0.45,
                depthWrite: false
            })
        );
        this.guideGroup.add(box);

        const edges = new THREE.LineSegments(
            new THREE.EdgesGeometry(new THREE.BoxGeometry(edgeWidth, edgeHeight, edgeDepth)),
            new THREE.LineBasicMaterial({ color: 0xbae6fd, transparent: true, opacity: 0.82 })
        );
        this.guideGroup.add(edges);

        const guideMaterial = new THREE.LineBasicMaterial({
            color: 0x93c5fd,
            transparent: true,
            opacity: guideOpacity,
            depthTest: true,
            depthWrite: false
        });
        const points: THREE.Vector3[] = [];
        for (let xi = 1; xi < x; xi++) {
            const wx = minX + xi;
            points.push(new THREE.Vector3(wx, minY, minZ), new THREE.Vector3(wx, maxY, minZ));
            points.push(new THREE.Vector3(wx, minY, maxZ), new THREE.Vector3(wx, maxY, maxZ));
            points.push(new THREE.Vector3(wx, minY, minZ), new THREE.Vector3(wx, minY, maxZ));
            points.push(new THREE.Vector3(wx, maxY, minZ), new THREE.Vector3(wx, maxY, maxZ));
        }
        for (let yi = 1; yi < y; yi++) {
            const wz = minZ + yi;
            points.push(new THREE.Vector3(minX, minY, wz), new THREE.Vector3(maxX, minY, wz));
            points.push(new THREE.Vector3(minX, maxY, wz), new THREE.Vector3(maxX, maxY, wz));
            points.push(new THREE.Vector3(minX, minY, wz), new THREE.Vector3(minX, maxY, wz));
            points.push(new THREE.Vector3(maxX, minY, wz), new THREE.Vector3(maxX, maxY, wz));
        }
        for (let zi = 1; zi < z; zi++) {
            const wy = minY + zi;
            points.push(new THREE.Vector3(minX, wy, minZ), new THREE.Vector3(maxX, wy, minZ));
            points.push(new THREE.Vector3(minX, wy, maxZ), new THREE.Vector3(maxX, wy, maxZ));
            points.push(new THREE.Vector3(minX, wy, minZ), new THREE.Vector3(minX, wy, maxZ));
            points.push(new THREE.Vector3(maxX, wy, minZ), new THREE.Vector3(maxX, wy, maxZ));
        }
        const guideGeometry = new THREE.BufferGeometry().setFromPoints(points);
        this.guideGroup.add(new THREE.LineSegments(guideGeometry, guideMaterial));
    }

    private syncBoardScene() {
        if (!this.boardScene || !this.boardCamera || !this.boardRenderer) return;
        this.clearGroup(this.workspaceGroup);
        this.clearGroup(this.placedGroup);
        this.clearGroup(this.dragGroup);
        this.clearGroup(this.ghostGroup);

        if (this.showReferenceSolution) {
            this.referenceSolution.forEach((record) => {
                const piece = this.pieces.find((item) => item.id === record.pieceId);
                if (!piece || piece.placed) return;
                const cells = this.getTransformedCells(piece, record.position, record.orientationIndex);
                this.addCellsToGroup(this.placedGroup, cells, piece.color, 'solution');
            });
        }

        this.pieces
            .filter((piece) => piece.status === 'workspace' && piece.workspacePosition && piece.id !== this.dragPieceId)
            .forEach((piece) => {
                this.addPieceAssemblyToGroup(this.workspaceGroup, piece, this.workspaceVector(piece), 'workspace');
            });

        this.placedPieces.forEach((record) => {
            const piece = this.pieces.find((item) => item.id === record.pieceId);
            if (!piece || piece.status !== 'placed') return;
            const cells = this.getTransformedCells(piece, record.position, record.orientationIndex);
            this.addCellsToGroup(this.placedGroup, cells, piece.color, 'solid', piece.id);
        });

        if (this.isDragging && this.draggingPiece) {
            if (this.dragHasBoardTarget) {
                this.addCellsToGroup(this.dragGroup, this.previewCells, this.draggingPiece.color, 'solid', this.draggingPiece.id);
            } else if (this.dragWorldPoint) {
                this.addPieceAssemblyToGroup(this.dragGroup, this.draggingPiece, this.dragWorldPoint, 'workspace');
            }
        }

        this.updateBoardCamera();
        this.boardRenderer.render(this.boardScene, this.boardCamera);
    }

    private syncPreviewScenes() {
        this.trayScenes.forEach((scene, index) => {
            this.renderPiecePreview(scene, this.pieces[index], 'tray');
        });
        this.renderPiecePreview(this.selectedScene, this.selectedPiece, 'selected');
        this.renderPiecePreview(this.dragScene, this.draggingPiece, 'drag');
    }

    private renderPiecePreview(sceneInfo: PreviewScene | undefined, piece: GamePiece | undefined, mode: 'tray' | 'selected' | 'drag') {
        if (!sceneInfo) return;
        this.clearGroup(sceneInfo.root);
        if (piece && (mode !== 'tray' || piece.status === 'tray')) {
            const orientationIndex = piece.id === this.selectedPieceId ? this.selectedOrientationIndex : piece.orientationIndex;
            const cells = piece.orientations[orientationIndex] || piece.orientations[0] || piece.cells;
            const centered = this.centerCellsForPreview(cells);
            this.addPreviewCells(sceneInfo.root, centered, piece.color);
            const scale = mode === 'selected' ? 1.12 : mode === 'drag' ? 1 : 0.96;
            sceneInfo.root.scale.setScalar(scale);
            sceneInfo.root.rotation.set(-0.58, 0.72, 0.18);
            sceneInfo.root.position.set(mode === 'tray' ? -0.42 : 0, mode === 'tray' ? 0.08 : 0, 0);
        }
        this.resizePreviewScene(sceneInfo);
        sceneInfo.renderer.render(sceneInfo.scene, sceneInfo.camera);
    }

    private addCellsToGroup(group: THREE.Group, cells: Coord[], color: string, mode: 'solid' | 'ghost' | 'solution', pieceId?: string) {
        const material = this.createCubeMaterial(color, mode);
        cells.forEach((cell) => {
            const mesh = new THREE.Mesh(new THREE.BoxGeometry(0.92, 0.92, 0.92), material);
            mesh.position.copy(this.cellToWorld(cell));
            mesh.castShadow = mode === 'solid';
            mesh.receiveShadow = true;
            if (pieceId) mesh.userData.pieceId = pieceId;
            group.add(mesh);
            const edges = new THREE.LineSegments(
                new THREE.EdgesGeometry(new THREE.BoxGeometry(0.925, 0.925, 0.925)),
                new THREE.LineBasicMaterial({
                    color: mode === 'ghost' ? 0xffffff : 0x0f172a,
                    transparent: true,
                    opacity: mode === 'ghost' ? 0.58 : 0.28
                })
            );
            edges.position.copy(mesh.position);
            if (pieceId) edges.userData.pieceId = pieceId;
            group.add(edges);
        });
    }

    private addPieceAssemblyToGroup(group: THREE.Group, piece: GamePiece, origin: THREE.Vector3, mode: 'workspace') {
        const orientation = piece.orientations[piece.orientationIndex] || piece.orientations[0] || piece.cells;
        const centered = this.centerCellsForPreview(orientation);
        const material = this.createCubeMaterial(piece.color, 'solid');
        centered.forEach((cell) => {
            const mesh = new THREE.Mesh(new THREE.BoxGeometry(0.74, 0.74, 0.74), material);
            mesh.position.set(origin.x + cell[0] * 0.8, origin.y + cell[2] * 0.8, origin.z + cell[1] * 0.8);
            mesh.castShadow = true;
            mesh.receiveShadow = true;
            mesh.userData.pieceId = piece.id;
            mesh.userData.mode = mode;
            group.add(mesh);
            const edges = new THREE.LineSegments(
                new THREE.EdgesGeometry(new THREE.BoxGeometry(0.745, 0.745, 0.745)),
                new THREE.LineBasicMaterial({ color: 0x0f172a, transparent: true, opacity: 0.3 })
            );
            edges.position.copy(mesh.position);
            edges.userData.pieceId = piece.id;
            edges.userData.mode = mode;
            group.add(edges);
        });
    }

    private addPreviewCells(group: THREE.Group, cells: Coord[], color: string) {
        const material = this.createCubeMaterial(color, 'solid');
        cells.forEach((cell) => {
            const mesh = new THREE.Mesh(new THREE.BoxGeometry(0.92, 0.92, 0.92), material);
            mesh.position.set(cell[0], cell[2], cell[1]);
            group.add(mesh);
            const edges = new THREE.LineSegments(
                new THREE.EdgesGeometry(new THREE.BoxGeometry(0.93, 0.93, 0.93)),
                new THREE.LineBasicMaterial({ color: 0x0f172a, transparent: true, opacity: 0.26 })
            );
            edges.position.copy(mesh.position);
            group.add(edges);
        });
    }

    private createCubeMaterial(color: string, mode: 'solid' | 'ghost' | 'solution') {
        return new THREE.MeshStandardMaterial({
            color: new THREE.Color(color),
            roughness: 0.44,
            metalness: 0.04,
            transparent: mode !== 'solid',
            opacity: mode === 'ghost' ? 0.42 : mode === 'solution' ? 0.22 : 1,
            depthWrite: mode === 'solid'
        });
    }

    private cellToWorld([x, y, z]: Coord): THREE.Vector3 {
        return new THREE.Vector3(
            x - (this.activeLevel.gridSize.x - 1) / 2,
            z - (this.activeLevel.gridSize.z - 1) / 2,
            y - (this.activeLevel.gridSize.y - 1) / 2
        );
    }

    private centerCellsForPreview(cells: Coord[]): Coord[] {
        const center = this.cellsCenter(cells);
        return cells.map(([x, y, z]) => [x - center[0], y - center[1], z - center[2]] as Coord);
    }

    private updateBoardCamera() {
        if (!this.boardCamera || !this.boardRenderer) return;
        const rect = this.boardRenderer.domElement.getBoundingClientRect();
        const aspect = rect.width && rect.height ? rect.width / rect.height : 1;
        this.boardCamera.aspect = aspect;
        this.boardCamera.updateProjectionMatrix();
        const yaw = this.cameraYaw * Math.PI / 180;
        const pitch = this.cameraPitch * Math.PI / 180;
        const largestDimension = Math.max(this.activeLevel.gridSize.x, this.activeLevel.gridSize.y, this.activeLevel.gridSize.z);
        const radius = (largestDimension * 4.5 + 1.7) / this.zoom;
        const target = this.getBoardFrameTarget();
        this.boardCamera.position.set(
            target.x + radius * Math.cos(pitch) * Math.sin(yaw),
            target.y + radius * Math.sin(pitch),
            target.z + radius * Math.cos(pitch) * Math.cos(yaw)
        );
        this.boardCamera.lookAt(target);
    }

    private getBoardFrameTarget(): THREE.Vector3 {
        const yaw = this.cameraYaw * Math.PI / 180;
        const cameraRight = new THREE.Vector3(Math.cos(yaw), 0, -Math.sin(yaw));
        return cameraRight.multiplyScalar(2.55).add(new THREE.Vector3(0, -1.85, 0));
    }

    private resizeBoardRenderer() {
        const host = this.boardHost?.nativeElement;
        if (!host || !this.boardRenderer) return;
        const width = Math.max(1, host.clientWidth);
        const height = Math.max(1, host.clientHeight);
        this.boardRenderer.setSize(width, height, false);
        this.updateBoardCamera();
    }

    private resizePreviewScene(sceneInfo: PreviewScene) {
        const width = Math.max(1, sceneInfo.host.clientWidth);
        const height = Math.max(1, sceneInfo.host.clientHeight);
        sceneInfo.renderer.setSize(width, height, false);
        sceneInfo.camera.aspect = width / height;
        sceneInfo.camera.updateProjectionMatrix();
    }

    private renderAllThree() {
        this.resizeBoardRenderer();
        this.syncBoardScene();
        this.syncPreviewScenes();
    }

    private queueThreeRender() {
        if (this.renderQueued) return;
        this.renderQueued = true;
        requestAnimationFrame(() => {
            this.renderQueued = false;
            this.renderAllThree();
        });
    }

    private clearGroup(group: THREE.Group) {
        while (group.children.length) {
            const child = group.children.pop();
            if (!child) continue;
            const mesh = child as THREE.Mesh;
            const geometry = mesh.geometry as THREE.BufferGeometry | undefined;
            const material = mesh.material as THREE.Material | THREE.Material[] | undefined;
            geometry?.dispose();
            if (Array.isArray(material)) material.forEach((item) => item.dispose());
            else material?.dispose();
        }
    }

    private disposeThree() {
        this.clearGroup(this.boardRoot);
        this.boardRenderer?.dispose();
        this.disposePreviewScenes(this.trayScenes);
        this.disposePreviewScenes([this.selectedScene, this.dragScene].filter((scene): scene is PreviewScene => Boolean(scene)));
    }

    private disposePreviewScenes(scenes: PreviewScene[]) {
        scenes.forEach((scene) => {
            this.clearGroup(scene.root);
            scene.renderer.dispose();
        });
    }

    private startTimer() {
        if (this.timer || this.activeLevel.status !== 'playable' || this.validationError || this.isComplete) return;
        this.timer = setInterval(() => {
            this.elapsedSeconds++;
            this.service.render();
        }, 1000);
    }

    private stopTimer() {
        if (!this.timer) return;
        clearInterval(this.timer);
        this.timer = null;
    }

    private validateLevelData(level: LevelData, pieces: GamePiece[]): boolean {
        const cellCount = pieces.reduce((total, piece) => total + piece.cells.length, 0);
        if (cellCount !== this.getTargetCellCount(level)) return false;
        const hasExpectedOrientations = pieces.every((piece) => {
            const expected = EXPECTED_ORIENTATION_COUNTS[piece.id];
            return expected === undefined || expected === piece.orientations.length;
        });
        return hasExpectedOrientations && this.hasBacktrackingSolution(level, pieces);
    }

    private hasBacktrackingSolution(level: LevelData, pieces: GamePiece[]): boolean {
        return this.findFirstSolution(level, pieces) !== null;
    }

    private findFirstSolution(level: LevelData, pieces: GamePiece[]): PlacementRecord[] | null {
        const placements = pieces.map((piece) => {
            const piecePlacements: SolverPlacement[] = [];
            piece.orientations.forEach((orientation, orientationIndex) => {
                for (let x = 0; x < level.gridSize.x; x++) {
                    for (let y = 0; y < level.gridSize.y; y++) {
                        for (let z = 0; z < level.gridSize.z; z++) {
                            const cells = orientation.map(([cx, cy, cz]) => [cx + x, cy + y, cz + z] as Coord);
                            if (cells.every(([cx, cy, cz]) => cx >= 0 && cy >= 0 && cz >= 0 && cx < level.gridSize.x && cy < level.gridSize.y && cz < level.gridSize.z)) {
                                piecePlacements.push({
                                    pieceId: piece.id,
                                    position: [x, y, z],
                                    orientationIndex,
                                    cellIndexes: cells.map(([cx, cy, cz]) => this.cellIndex(cx, cy, cz, level.gridSize))
                                });
                            }
                        }
                    }
                }
            });
            return piecePlacements;
        });
        const chosen = new Map<string, PlacementRecord>();
        const used = new Array(this.getTargetCellCount(level)).fill(false);
        const remaining = new Set(placements.map((_, index) => index));
        const search = (): boolean => {
            if (!remaining.size) return true;
            let nextCell = used.findIndex((filled) => !filled);
            if (nextCell < 0) return true;
            const candidates: Array<{ pieceIndex: number; placement: SolverPlacement }> = [];
            remaining.forEach((pieceIndex) => {
                placements[pieceIndex]
                    .filter((placement) => placement.cellIndexes.includes(nextCell) && placement.cellIndexes.every((index) => !used[index]))
                    .forEach((placement) => candidates.push({ pieceIndex, placement }));
            });
            if (!candidates.length) return false;
            for (const { pieceIndex, placement } of candidates) {
                if (!remaining.has(pieceIndex)) continue;
                remaining.delete(pieceIndex);
                placement.cellIndexes.forEach((index) => {
                    used[index] = true;
                });
                chosen.set(placement.pieceId, {
                    pieceId: placement.pieceId,
                    position: placement.position,
                    orientationIndex: placement.orientationIndex
                });
                if (search()) return true;
                chosen.delete(placement.pieceId);
                placement.cellIndexes.forEach((index) => {
                    used[index] = false;
                });
                remaining.add(pieceIndex);
            }
            return false;
        };
        if (!search()) return null;
        return pieces
            .map((piece) => chosen.get(piece.id))
            .filter((record): record is PlacementRecord => Boolean(record));
    }

    private cubeTransform(cell: Coord, gap: number): string {
        const centerX = (this.activeLevel.gridSize.x - 1) / 2;
        const centerY = (this.activeLevel.gridSize.y - 1) / 2;
        const centerZ = (this.activeLevel.gridSize.z - 1) / 2;
        return this.freeCubeTransform([cell[0] - centerX, cell[1] - centerY, cell[2] - centerZ], gap);
    }

    private freeCubeTransform(cell: Coord, gap: number): string {
        const x = cell[0] * gap;
        const y = cell[1] * gap;
        const z = cell[2] * gap;
        return `translate3d(${x}px, ${y}px, ${z}px)`;
    }

    private cellsCenter(cells: Coord[]): Coord {
        const minX = Math.min(...cells.map(([x]) => x));
        const maxX = Math.max(...cells.map(([x]) => x));
        const minY = Math.min(...cells.map(([, y]) => y));
        const maxY = Math.max(...cells.map(([, y]) => y));
        const minZ = Math.min(...cells.map(([, , z]) => z));
        const maxZ = Math.max(...cells.map(([, , z]) => z));
        return [(minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2];
    }

    private pickMovablePieceId(event: PointerEvent): string {
        if (!this.boardCamera || !this.boardRenderer) return '';
        const pickableObjects = [
            ...this.workspaceGroup.children,
            ...this.placedGroup.children
        ];
        if (!pickableObjects.length) return '';
        const rect = this.boardRenderer.domElement.getBoundingClientRect();
        if (!rect.width || !rect.height) return '';
        this.updateBoardCamera();
        const ndc = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1
        );
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(ndc, this.boardCamera);
        const hit = raycaster.intersectObjects(pickableObjects, false)
            .find((item) => item.object.userData?.pieceId);
        return hit?.object.userData.pieceId || '';
    }

    private pickupMovablePiece(pieceId: string, event: PointerEvent) {
        const piece = this.pieces.find((item) => item.id === pieceId);
        if (!piece) return;
        if (piece.status === 'placed') {
            this.pickupPlacedPiece(piece, event);
            return;
        }
        if (piece.status === 'workspace') {
            this.selectedPieceId = piece.id;
            this.selectedOrientationIndex = piece.orientationIndex;
            this.startDrag(piece, event);
        }
    }

    private pickupPlacedPiece(piece: GamePiece, event: PointerEvent) {
        const pieceId = piece.id;
        const record = this.placedPieces.find((item) => item.pieceId === pieceId);
        if (!record) return;
        this.removePiece(this.board, pieceId);
        this.placedPieces = this.placedPieces.filter((item) => item.pieceId !== pieceId);
        this.history = this.history.filter((item) => item.pieceId !== pieceId);
        piece.placed = false;
        piece.status = 'workspace';
        piece.position = undefined;
        piece.orientationIndex = record.orientationIndex;
        piece.workspacePosition = this.findWorkspacePoint(event.clientX, event.clientY) || this.defaultWorkspacePosition(piece);
        this.selectedPieceId = piece.id;
        this.selectedOrientationIndex = piece.orientationIndex;
        this.selectedPosition = { x: record.position[0], y: record.position[1], z: record.position[2] };
        this.isComplete = false;
        this.startDrag(piece, event);
    }

    private movePieceToWorkspace(piece: GamePiece, point: THREE.Vector3) {
        piece.placed = false;
        piece.status = 'workspace';
        piece.position = undefined;
        piece.workspacePosition = this.resolveWorkspacePosition(piece, point);
        this.selectedPieceId = piece.id;
        this.selectedOrientationIndex = piece.orientationIndex;
        this.queueThreeRender();
    }

    private workspaceVector(piece: GamePiece): THREE.Vector3 {
        const position = piece.workspacePosition || this.defaultWorkspacePosition(piece);
        return new THREE.Vector3(position[0], position[1], position[2]);
    }

    private workspaceVectorFromCoord(position: Coord): THREE.Vector3 {
        return new THREE.Vector3(position[0], position[1], position[2]);
    }

    private defaultWorkspacePosition(piece: GamePiece): Coord {
        const index = Math.max(0, this.pieces.findIndex((item) => item.id === piece.id));
        const lane = index % 4;
        return [2.8 + lane * 1.08, -1.7, -1.45 + Math.floor(index / 4) * 1.1];
    }

    private resolveWorkspacePosition(piece: GamePiece, desired: THREE.Vector3): Coord {
        const offsets: Coord[] = [
            [0, 0, 0],
            [1.35, 0, 0],
            [-1.35, 0, 0],
            [0, 0, 1.35],
            [0, 0, -1.35],
            [1.35, 0, 1.35],
            [-1.35, 0, 1.35],
            [1.35, 0, -1.35],
            [-1.35, 0, -1.35],
            [2.7, 0, 0],
            [-2.7, 0, 0]
        ];
        const candidate = offsets
            .map(([x, y, z]) => this.clampWorkspacePoint(desired.clone().add(new THREE.Vector3(x, y, z))))
            .find((point) => !this.workspaceOverlaps(piece, point));
        const resolved = candidate || this.clampWorkspacePoint(desired);
        return [resolved.x, resolved.y, resolved.z];
    }

    private workspaceOverlaps(piece: GamePiece, point: THREE.Vector3): boolean {
        const radius = this.workspaceRadius(piece);
        return this.pieces.some((other) => {
            if (other.id === piece.id || other.status !== 'workspace' || !other.workspacePosition) return false;
            const otherPoint = this.workspaceVector(other);
            const distance = Math.hypot(point.x - otherPoint.x, point.z - otherPoint.z);
            return distance < radius + this.workspaceRadius(other);
        });
    }

    private workspaceRadius(piece: GamePiece): number {
        const orientation = piece.orientations[piece.orientationIndex] || piece.orientations[0] || piece.cells;
        if (!orientation.length) return 0.9;
        const xs = orientation.map(([x]) => x);
        const zs = orientation.map(([, y]) => y);
        const span = Math.max(Math.max(...xs) - Math.min(...xs) + 1, Math.max(...zs) - Math.min(...zs) + 1);
        return 0.48 + span * 0.34;
    }

    private clampWorkspacePoint(point: THREE.Vector3): THREE.Vector3 {
        point.x = Math.max(-5.4, Math.min(5.4, point.x));
        point.y = -1.75;
        point.z = Math.max(-4.4, Math.min(4.4, point.z));
        if (Math.abs(point.x) < 1.9 && Math.abs(point.z) < 1.9) {
            point.x = point.x < 0 ? -2.35 : 2.35;
        }
        return point;
    }

    private findWorkspacePoint(clientX: number, clientY: number): THREE.Vector3 | null {
        if (!this.boardCamera || !this.boardRenderer) return null;
        const rect = this.boardRenderer.domElement.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;
        this.updateBoardCamera();
        const ndc = new THREE.Vector2(
            ((clientX - rect.left) / rect.width) * 2 - 1,
            -((clientY - rect.top) / rect.height) * 2 + 1
        );
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(ndc, this.boardCamera);
        const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 1.75);
        const point = new THREE.Vector3();
        if (!raycaster.ray.intersectPlane(plane, point)) return null;
        return this.clampWorkspacePoint(point);
    }

    private findNearestSnap(clientX: number, clientY: number, rect: DOMRect): Coord | null {
        const piece = this.activeInteractionPiece;
        if (!piece) return null;
        const raySnap = this.findRaycastSnap(clientX, clientY);
        if (raySnap) return raySnap;

        let best: { position: Coord; score: number } | null = null;
        const candidates = this.getSnapCandidates(piece);

        candidates.forEach((position) => {
            const projected = this.projectGridToScreen(position, rect);
            const dx = clientX - projected.x;
            const dy = clientY - projected.y;
            const score = dx * dx + dy * dy;
            if (!best || score < best.score) {
                best = { position, score };
            }
        });

        return best?.position || null;
    }

    private findRaycastSnap(clientX: number, clientY: number): Coord | null {
        if (!this.boardCamera || !this.boardRenderer || !this.activeInteractionPiece) return null;
        this.updateBoardCamera();
        const rect = this.boardRenderer.domElement.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;

        const ndc = new THREE.Vector2(
            ((clientX - rect.left) / rect.width) * 2 - 1,
            -((clientY - rect.top) / rect.height) * 2 + 1
        );
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(ndc, this.boardCamera);
        const { origin, direction } = raycaster.ray;
        const axes: Array<'x' | 'y' | 'z'> = ['x', 'y', 'z'];
        const boundsByAxis: Record<'x' | 'y' | 'z', number> = {
            x: this.activeLevel.gridSize.x / 2,
            y: this.activeLevel.gridSize.z / 2,
            z: this.activeLevel.gridSize.y / 2
        };
        let best: { point: THREE.Vector3; distance: number } | null = null;

        axes.forEach((axis) => {
            const bounds = boundsByAxis[axis];
            [-bounds, bounds].forEach((side) => {
                const component = direction[axis];
                if (Math.abs(component) < 0.0001) return;
                const distance = (side - origin[axis]) / component;
                if (distance <= 0) return;
                const point = origin.clone().add(direction.clone().multiplyScalar(distance));
                const inside = axes
                    .filter((item) => item !== axis)
                    .every((item) => point[item] >= -boundsByAxis[item] - 0.08 && point[item] <= boundsByAxis[item] + 0.08);
                if (!inside) return;
                if (!best || distance < best.distance) {
                    best = { point, distance };
                }
            });
        });

        return best ? this.worldPointToGridSnap(best.point) : null;
    }

    private worldPointToGridSnap(point: THREE.Vector3): Coord {
        return this.clampPositionForCurrentOrientation([
            Math.round(point.x + (this.activeLevel.gridSize.x - 1) / 2),
            Math.round(point.z + (this.activeLevel.gridSize.y - 1) / 2),
            Math.round(point.y + (this.activeLevel.gridSize.z - 1) / 2)
        ]);
    }

    private getSnapCandidates(piece: GamePiece): Coord[] {
        const orientation = piece.orientations[this.orientationIndexForPiece(piece)] || piece.orientations[0] || [];
        const maxCell = this.maxCellForOrientation(orientation);
        const candidates: Coord[] = [];
        for (let x = 0; x <= this.activeLevel.gridSize.x - 1 - maxCell[0]; x++) {
            for (let y = 0; y <= this.activeLevel.gridSize.y - 1 - maxCell[1]; y++) {
                for (let z = 0; z <= this.activeLevel.gridSize.z - 1 - maxCell[2]; z++) {
                    candidates.push([x, y, z]);
                }
            }
        }
        return candidates;
    }

    private clampPositionForCurrentOrientation(position: Coord): Coord {
        const piece = this.activeInteractionPiece;
        if (!piece) return position;
        const orientation = piece.orientations[this.orientationIndexForPiece(piece)] || piece.orientations[0] || [];
        const maxCell = this.maxCellForOrientation(orientation);
        return [
            Math.max(0, Math.min(this.activeLevel.gridSize.x - 1 - maxCell[0], position[0])),
            Math.max(0, Math.min(this.activeLevel.gridSize.y - 1 - maxCell[1], position[1])),
            Math.max(0, Math.min(this.activeLevel.gridSize.z - 1 - maxCell[2], position[2]))
        ];
    }

    private maxCellForOrientation(cells: Coord[]): Coord {
        if (!cells.length) return [0, 0, 0];
        return [
            Math.max(...cells.map(([x]) => x)),
            Math.max(...cells.map(([, y]) => y)),
            Math.max(...cells.map(([, , z]) => z))
        ];
    }

    private projectGridToScreen(point: Coord, rect: DOMRect) {
        if (this.boardCamera) {
            this.updateBoardCamera();
            const projected = this.cellToWorld(point).clone().project(this.boardCamera);
            return {
                x: rect.left + (projected.x + 1) * rect.width / 2,
                y: rect.top + (-projected.y + 1) * rect.height / 2
            };
        }
        const rotated = this.rotateViewPoint(point[0] - 1, point[1] - 1, point[2] - 1);
        const scale = 92 * this.zoom;
        return {
            x: rect.left + rect.width / 2 + rotated.x * scale,
            y: rect.top + rect.height * 0.5 + rotated.y * scale
        };
    }

    private rotateViewPoint(x: number, y: number, z: number) {
        const yaw = this.cameraYaw * Math.PI / 180;
        const pitch = this.cameraPitch * Math.PI / 180;
        const yawCos = Math.cos(yaw);
        const yawSin = Math.sin(yaw);
        const pitchCos = Math.cos(pitch);
        const pitchSin = Math.sin(pitch);
        const yawX = x * yawCos - y * yawSin;
        const yawY = x * yawSin + y * yawCos;
        return {
            x: yawX,
            y: yawY * pitchCos - z * pitchSin
        };
    }

    private getValidPlacements(level: LevelData, piece: GamePiece): PlacementRecord[] {
        const placements: PlacementRecord[] = [];
        piece.orientations.forEach((orientation, orientationIndex) => {
            for (let x = 0; x < level.gridSize.x; x++) {
                for (let y = 0; y < level.gridSize.y; y++) {
                    for (let z = 0; z < level.gridSize.z; z++) {
                        const position: Coord = [x, y, z];
                        if (this.canPlacePiece(this.board, orientation, position, level)) {
                            placements.push({ pieceId: piece.id, position, orientationIndex });
                        }
                    }
                }
            }
        });
        return placements;
    }

    private buildOrientationAudit(pieces: GamePiece[]): string {
        return pieces.map((piece) => {
            const expected = EXPECTED_ORIENTATION_COUNTS[piece.id];
            const suffix = expected ? `/${expected}` : '';
            return `${piece.symbol}:${piece.orientations.length}${suffix}`;
        }).join('  ');
    }

    private isGuideCell([x, y, z]: Coord): boolean {
        const max = this.activeLevel.gridSize;
        return x === 0 || y === 0 || z === 0 || x === max.x - 1 || y === max.y - 1 || z === max.z - 1;
    }

    private isSparseGuideCell([x, y, z]: Coord): boolean {
        const max = this.activeLevel.gridSize;
        const boundaryCount = [x === 0 || x === max.x - 1, y === 0 || y === max.y - 1, z === 0 || z === max.z - 1]
            .filter(Boolean).length;
        return boundaryCount >= 2;
    }

    private clampGrid(value: number, axis: Axis): number {
        return Math.max(0, Math.min(this.activeLevel.gridSize[axis] - 1, value));
    }

    private cellIndex(x: number, y: number, z: number, gridSize: GridSize): number {
        return x + gridSize.x * y + gridSize.x * gridSize.y * z;
    }

    private cellsKey(cells: Coord[]): string {
        return cells.map((cell) => cell.join(',')).sort().join(';');
    }
}

export default PageGameComponent;